import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ModalComponent} from "../../../../utils/modal/modal.component";
import {SpinnerService} from "../../../../utils/services/spinner.service";
import {forkJoin, map, Observable, of, switchMap, tap} from "rxjs";
import {PersonUtils} from "../../../../auth_profile/utils/v3/person-utils";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {PersonContactCacheService} from "../../../services/person-contact-cache.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {Pageable} from "../../../../utils/pageable";
import {V3SchoolTeacher} from "../../../../auth_profile/model/v3/schools";

@Component({
    selector: 'app-calendar-teacher-filter-modal',
    templateUrl: './calendar-teacher-filter-modal.component.html',
    styleUrls: ['./calendar-teacher-filter-modal.component.scss'],
    standalone: false
})
export class CalendarTeacherFilterModalComponent implements OnInit {
  @ViewChild('filterTeacherModal', { static: true }) filterModal: ModalComponent;
  teachers: V3SchoolTeacher[];
  selectedTeacherIds: number[] = [];
  private _availableTeachersIds: number[];
  private _studentId: number;
  private schoolId: number | null = null;

  @Input()
  set studentId(value: number) {
    this._studentId = value;
    this.updateTeachersList();
  }
  get studentId() {
    return this._studentId;
  }
  @Input()
  set availableTeachersIds(teachersIds: number[]) {
    this._availableTeachersIds = teachersIds;
    this.updateTeachersList();
  }
  @Output()
  selectedTeachersUpdate = new EventEmitter<number[]>();

  constructor(
    private spinner: SpinnerService,
    private apiProvider: NativeServiceApiProvider,
    private personContactCache: PersonContactCacheService,
    private authProvider: AuthorizationServiceProvider
  ) {}

  ngOnInit(): void {}

  open() {
    this.filterModal.show(true);
  }

  updateTeachersList(): void {
    if (!this.studentId) {
      return;
    }

    if (!this._availableTeachersIds?.length) {
      this.teachers = null;
      return;
    }

    this.getTeachers()
      .pipe(switchMap((teachers) => this.preloadTeachersPhotos(teachers)))
      .subscribe();
  }

  private ensureSchoolId(): Observable<number> {
    if (this.schoolId != null) {
      return of(this.schoolId);
    }
    return this.authProvider.getAuthDetailsService().pipe(
      switchMap((authApi) => authApi.getSelfSchoolId()),
      tap((schoolId) => (this.schoolId = schoolId))
    );
  }

  getTeachers(): Observable<V3SchoolTeacher[]> {
    return this.spinner.trace<V3SchoolTeacher[]>(
      this.ensureSchoolId().pipe(
        switchMap((schoolId) =>
          this.apiProvider.casa().pipe(
            switchMap((api) =>
              api.getTeachers(
                schoolId,
                null,
                null,
                this._availableTeachersIds,
                Pageable.of(0, 500, null)
              )
            ),
            map((page) => page?.content ?? []),
            tap((teachers) => (this.teachers = teachers))
          )
        )
      )
    );
  }

  selectTeacher(profile: V3SchoolTeacher) {
    if (!profile) {
      this.selectedTeacherIds = [];
      return;
    }
    const teacherFoundIndex = this.selectedTeacherIds.indexOf(profile.id);
    if (teacherFoundIndex >= 0) {
      this.selectedTeacherIds.splice(teacherFoundIndex, 1);
      return;
    }
    this.selectedTeacherIds.push(profile.id);
  }

  isActive(teacher: V3SchoolTeacher) {
    return this.selectedTeacherIds.includes(teacher.id);
  }

  getTeacherName(teacherProfile: V3SchoolTeacher) {
    return teacherProfile
      ? PersonUtils.getPersonName(teacherProfile.person.details)
      : '';
  }

  getTeacherPhoto(teacherProfile: V3SchoolTeacher) {
    return this.personContactCache.getPhotoUrlSync(teacherProfile?.person?.id);
  }

  getSelectedTeachers() {
    return this.selectedTeacherIds;
  }

  getInitials(teacherProfile: V3SchoolTeacher) {
    return PersonUtils.getInitials(teacherProfile.person.details);
  }

  applyFilters() {
    this.selectedTeachersUpdate.emit(this.selectedTeacherIds);
    this.filterModal.hide();
  }

  clearFilters() {
    this.selectTeacher(null);
    this.filterModal.hide();
  }

  private preloadTeachersPhotos(teachers: V3SchoolTeacher[]): Observable<V3SchoolTeacher[]> {
    if (!teachers?.length) {
      return of(teachers);
    }
    return forkJoin(
      teachers.map((teacher) =>
        this.personContactCache.getPhotoUrl(teacher?.person?.id).pipe(map(() => null))
      )
    ).pipe(map(() => teachers));
  }
}
